// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $Model,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Col,
  Row,
} from 'reactstrap';
import {
  AvField,
} from 'availity-reactstrap-validation';
import {
  InputDateTime,
  InputFile,
  InputSwitch,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getRegionSeasonDateRange,
} from '../../../helpers';
import config from '../../../config';

// Types
import type {
  $ReducerState,
} from '../../../redux';

type $OptionalProps = {
  onChange?: () => unknown;
};

export type $OwnProps = $OptionalProps & $Translate & {
  item: $FullAdminRegatta;
  referenceData: Array<$Model<'Regatta'>>;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

const RegattaFieldsView = function ({
  item,
  main,
  onChange,
  referenceData,
  t,
}: $Props) {
  return (
    <Row>
      <Col sm={8}>
        <Row>
          <Col
            md={4}
            sm={12}
          >
            <InputFile
              fileDirUrl={`${config.CDN_URL}/images/max`}
              imageThumbDirUrl={`${config.CDN_URL}/images/min`}
              label={t('Logo')}
              labelChangeFile={t('Change file')}
              labelError={t('Error')}
              labelFile={t('File')}
              name={'Image'}
              onChange={onChange}
              onError={ErrorLog.exception}
              uploadUrl={`${config.CDN_URL}/v1.0/upload/image`}
              value={item.Image}
            />
          </Col>
          <Col
            md={8}
            sm={12}
          >
            <AvField
              label={t('Title')}
              name={'Name'}
              onChange={onChange}
              required
              value={item.Name}
            />
          </Col>
        </Row>
        <Row>
          <Col
            lg={4}
            md={6}
          >
            <InputDateTime
              label={t('Start date')}
              labelOpenCalendar={t('Open calendar')}
              name={'StartDate'}
              onChange={onChange}
              range={getRegionSeasonDateRange({
                main,
              })}
              required
              timezone={main.timezone}
              value={item.StartDate}
            />
          </Col>
          <Col
            lg={4}
            md={6}
          >
            <InputDateTime
              label={t('End date')}
              labelOpenCalendar={t('Open calendar')}
              name={'EndDate'}
              onChange={onChange}
              range={getRegionSeasonDateRange({
                main,
              })}
              required
              timezone={main.timezone}
              value={item.EndDate}
            />
          </Col>
          <Col
            lg={4}
            md={12}
          >
            <AvField
              label={t('Last year regatta')}
              name={'ReferenceId'}
              onChange={onChange}
              type={'select'}
              value={item.ReferenceId}
            >
              <option />
              {_.map(
                referenceData,
                (i) => (
                  <option
                    key={i.id}
                    value={i.id}
                  >
                    {i.Name}
                  </option>
                ),
              )}
            </AvField>
          </Col>
        </Row>
        <Row>
          <Col
            lg={4}
            md={6}
          >
            <AvField
              label={`${t('Location')} (${t('City')})`}
              name={'Location'}
              onChange={onChange}
              required
              value={item.Location}
            />
          </Col>
          <Col
            lg={8}
            md={12}
          >
            <AvField
              label={t('Event place')}
              name={'GeoLocation'}
              onChange={onChange}
              value={item.GeoLocation || ''}
            />
          </Col>
        </Row>
        <hr />
        <Row>
          <Col className={'d-flex justify-content-between align-items-center border-thin-top py-1'}>
            {t('Use bow numbers')}
            <InputSwitch
              name={'BowNoEnabled'}
              onChange={onChange}
              value={item.BowNoEnabled}
            />
          </Col>
        </Row>
        <Row>
          <Col className={'d-flex justify-content-between align-items-center border-thin-top py-1'}>
            {t('Allow entries to submit protests')}
            <InputSwitch
              name={'SubmitProtestEnabled'}
              onChange={onChange}
              value={item.SubmitProtestEnabled}
            />
          </Col>
        </Row>
      </Col>
      <Col sm={4}>
        <Row>
          <Col>
            <AvField
              label={t('Organizer name')}
              name={'OrganizerName'}
              onChange={onChange}
              value={item.OrganizerName || ''}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <AvField
              label={t('Organizer email')}
              name={'OrganizerEmail'}
              onChange={onChange}
              required
              type={'email'}
              value={item.OrganizerEmail}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <AvField
              label={t('Aditional organizer contact info')}
              name={'OrganizerContact'}
              onChange={onChange}
              rows={'3'}
              type={'textarea'}
              value={item.OrganizerContact || ''}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

RegattaFieldsView.defaultProps = {
  onChange: undefined,
};

export default RegattaFieldsView;
